<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.shops.create_title') }}
          </h1>
        </div>
        <div class="d-flex align-items-center gap-2">
          <router-link to="/landlord/shops" class="btn btn-sm btn-light">
            <i class="fas fa-arrow-left me-1"></i> {{ $t('landlord.shops.back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">
        <form @submit.prevent="onSubmit">

          <!-- Société -->
          <div class="card card-flush mb-8">
            <div class="card-header border-0 pt-6">
              <h3 class="card-title fw-bolder">{{ $t('landlord.societies.section_title') }}</h3>
            </div>
            <div class="card-body py-4">
              <div class="d-flex gap-5 mb-5">
                <label class="form-check form-check-custom">
                  <input class="form-check-input" type="radio" value="new" v-model="societyMode" :disabled="lockedToSociety" />
                  <span class="form-check-label">{{ $t('landlord.societies.mode_new') }}</span>
                </label>
                <label class="form-check form-check-custom">
                  <input class="form-check-input" type="radio" value="existing" v-model="societyMode" :disabled="lockedToSociety" />
                  <span class="form-check-label">{{ $t('landlord.societies.mode_existing') }}</span>
                </label>
              </div>

              <div v-if="societyMode === 'existing'">
                <div v-if="form.society_id && selectedSociety" class="d-flex align-items-center gap-3 p-3 bg-light-primary rounded">
                  <i class="fas fa-building text-primary fs-2"></i>
                  <div class="flex-grow-1">
                    <div class="fw-bold">{{ selectedSociety.name }}</div>
                    <div class="text-muted fs-8">{{ selectedSociety.email }} — {{ selectedSociety.shops_count }} {{ $t('landlord.societies.col_shops_count') }}</div>
                  </div>
                  <button v-if="!lockedToSociety" type="button" class="btn btn-sm btn-light" @click="clearSociety">
                    {{ $t('landlord.societies.change') }}
                  </button>
                </div>
                <div v-else>
                  <input type="text" v-model="societySearch" @input="onSocietySearch"
                         class="form-control form-control-solid mb-3"
                         :placeholder="$t('landlord.societies.search_placeholder')" />
                  <div v-if="societyResults.length" class="list-group">
                    <button v-for="society in societyResults" :key="society.id" type="button"
                            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            @click="selectSociety(society)">
                      <span>{{ society.name }} <span class="text-muted fs-8">({{ society.email }})</span></span>
                      <span class="badge badge-light-primary">{{ society.shops_count }} {{ $t('landlord.societies.col_shops_count') }}</span>
                    </button>
                  </div>
                </div>
                <span v-if="errors.society_id" class="text-danger fs-8 d-block mt-2">{{ errors.society_id[0] }}</span>
              </div>
            </div>
          </div>

          <!-- Boutique -->
          <div class="card card-flush mb-8">
            <div class="card-header border-0 pt-6">
              <h3 class="card-title fw-bolder">{{ $t('landlord.shops.section_shop') }}</h3>
            </div>
            <div class="card-body py-4">
              <div class="row g-5">
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.name') }}</label>
                  <input v-model="form.name" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.name" class="text-danger fs-8">{{ errors.name[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.domain') }}</label>
                  <input v-model="form.domain" @input="domainTouched = true" type="text" class="form-control form-control-solid"
                         :placeholder="$t('landlord.shops.fields.domain_placeholder')" />
                  <span class="form-text text-muted fs-8">{{ $t('landlord.shops.domain_auto_hint') }}</span>
                  <span v-if="errors.domain" class="text-danger fs-8 d-block">{{ errors.domain[0] }}</span>
                </div>
                <div class="col-md-6" v-if="societyMode === 'new'">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.society_email') }}</label>
                  <input v-model="form.society_email" type="email" class="form-control form-control-solid" />
                  <span v-if="errors.society_email" class="text-danger fs-8">{{ errors.society_email[0] }}</span>
                </div>
                <div class="col-md-6" v-if="societyMode === 'new'">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.ifu_number') }}</label>
                  <input v-model="form.ifu_number" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.ifu_number" class="text-danger fs-8">{{ errors.ifu_number[0] }}</span>
                </div>
                <div class="col-md-6" v-if="societyMode === 'new'">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.rccm_number') }}</label>
                  <input v-model="form.rccm_number" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.rccm_number" class="text-danger fs-8">{{ errors.rccm_number[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.address') }}</label>
                  <input v-model="form.address" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.address" class="text-danger fs-8">{{ errors.address[0] }}</span>
                </div>
                <div class="col-md-6">
                  <InputTel
                    name="phone"
                    :label="$t('landlord.shops.fields.phone')"
                    containerClass=""
                    labelClass="form-label fs-7 fw-bold"
                    v-model:phone="form.phone"
                    v-model:phone-code="phoneCodes.phone_code"
                  />
                  <span v-if="errors.phone" class="text-danger fs-8">{{ errors.phone[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.country') }}</label>
                  <select v-model="form.country_id" class="form-select form-select-solid">
                    <option value="">--</option>
                    <option v-for="country in countries" :key="country.id" :value="country.id">
                      {{ country.name }}
                    </option>
                  </select>
                  <span v-if="errors.country_id" class="text-danger fs-8">{{ errors.country_id[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.sector') }}</label>
                  <select v-model="form.sector_id" class="form-select form-select-solid">
                    <option value="">{{ $t('landlord.shops.fields.sector_placeholder') }}</option>
                    <option v-for="sector in sectors" :key="sector.id" :value="sector.id">
                      {{ sector.name }}
                    </option>
                  </select>
                  <span v-if="errors.sector_id" class="text-danger fs-8">{{ errors.sector_id[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold d-block mb-3">{{ $t('landlord.shops.fields.logo') }}</label>
                  <div class="image-input image-input-outline" data-kt-image-input="true">
                    <div class="image-input-wrapper w-125px h-125px"
                         :style="{ backgroundImage: 'url(' + (files.logo ? logoPreviewing : defaultImage) + ')' }"></div>
                    <label class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                           data-kt-image-input-action="change" title="Changer le logo">
                      <i class="bi bi-pencil-fill fs-7"></i>
                      <input type="file" @change="onFileChange($event, 'logo')" accept=".png,.jpg,.jpeg,.svg" />
                      <input type="hidden" name="logo_remove" />
                    </label>
                    <span class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                          @click="removeImageFile('logo')" title="Supprimer">
                      <i class="bi bi-x fs-2"></i>
                    </span>
                  </div>
                  <span class="text-muted fs-8 d-block mt-2">{{ $t('landlord.shops.fields.logo_hint') }}</span>
                  <span v-if="errors.logo" class="text-danger fs-8 d-block">{{ errors.logo[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold d-block mb-3">{{ $t('landlord.shops.fields.favicon') }}</label>
                  <div class="image-input image-input-outline" data-kt-image-input="true">
                    <div class="image-input-wrapper w-65px h-65px"
                         :style="{ backgroundImage: 'url(' + (files.favicon ? faviconPreviewing : defaultImage) + ')' }"></div>
                    <label class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                           data-kt-image-input-action="change" title="Changer le favicon">
                      <i class="bi bi-pencil-fill fs-7"></i>
                      <input type="file" @change="onFileChange($event, 'favicon')" accept=".png,.jpg,.jpeg,.ico,.svg" />
                      <input type="hidden" name="favicon_remove" />
                    </label>
                    <span class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                          @click="removeImageFile('favicon')" title="Supprimer">
                      <i class="bi bi-x fs-2"></i>
                    </span>
                  </div>
                  <span class="text-muted fs-8 d-block mt-2">{{ $t('landlord.shops.fields.favicon_hint') }}</span>
                  <span v-if="errors.favicon" class="text-danger fs-8 d-block">{{ errors.favicon[0] }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Administrateur -->
          <div class="card card-flush mb-8" v-if="societyMode === 'new'">
            <div class="card-header border-0 pt-6">
              <h3 class="card-title fw-bolder">{{ $t('landlord.shops.section_admin') }}</h3>
            </div>
            <div class="card-body py-4">
              <div class="row g-5">
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.admin_lastname') }}</label>
                  <input v-model="form.admin_lastname" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.admin_lastname" class="text-danger fs-8">{{ errors.admin_lastname[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.admin_firstname') }}</label>
                  <input v-model="form.admin_firstname" type="text" class="form-control form-control-solid" />
                  <span v-if="errors.admin_firstname" class="text-danger fs-8">{{ errors.admin_firstname[0] }}</span>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.admin_email') }}</label>
                  <input v-model="form.admin_email" type="email" class="form-control form-control-solid" />
                  <span v-if="errors.admin_email" class="text-danger fs-8">{{ errors.admin_email[0] }}</span>
                </div>
                <div class="col-md-6">
                  <InputTel
                    name="admin_phone"
                    :label="$t('landlord.shops.fields.admin_phone')"
                    containerClass=""
                    labelClass="form-label fs-7 fw-bold"
                    v-model:phone="form.admin_phone"
                    v-model:phone-code="phoneCodes.admin_phone_code"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-7 fw-bold required">{{ $t('landlord.shops.fields.admin_password') }}</label>
                  <div class="position-relative">
                    <input
                        v-model="form.admin_password"
                        :type="showAdminPassword ? 'text' : 'password'"
                        class="form-control form-control-solid pe-10"
                    />
                    <span
                        class="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer"
                        @click="showAdminPassword = !showAdminPassword"
                    >
                      <i :class="showAdminPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </span>
                  </div>
                  <span v-if="errors.admin_password" class="text-danger fs-8">{{ errors.admin_password[0] }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit -->
          <div class="d-flex justify-content-end gap-3 mb-10">
            <router-link to="/landlord/shops" class="btn btn-light">
              {{ $t('landlord.shops.cancel') }}
            </router-link>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('landlord.shops.create_submit') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import InputTel from '@/components/InputTel.vue'

export default {
    name: 'LandlordShopCreate',
    components: { InputTel },
    data() {
        return {
            submitting: false,
            showAdminPassword: false,
            errors: {},
            societyMode: 'new',
            lockedToSociety: false,
            societySearch: '',
            societySearchTimeout: null,
            societyResults: [],
            selectedSociety: null,
            form: {
                name: '',
                domain: '',
                address: '',
                phone: '',
                country_id: '',
                sector_id: '',
                society_email: '',
                society_id: '',
                ifu_number: '',
                rccm_number: '',
                admin_lastname: '',
                admin_firstname: '',
                admin_email: '',
                admin_phone: '',
                admin_password: '',
            },
            phoneCodes: {
                phone_code: '',
                admin_phone_code: '',
            },
            files: {
                logo: null,
                favicon: null,
            },
            logoPreviewing: null,
            faviconPreviewing: null,
            defaultImage: '/assets/media/svg/files/blank-image.svg',
            domainTouched: false,
            // Doit refléter PLATFORM_DOMAIN (api-shop/.env) — seulement pour
            // l'aperçu affiché ici. Si le champ est laissé vide à la
            // soumission, c'est le backend qui génère réellement le
            // sous-domaine à partir de sa propre config (source de vérité) ;
            // un éventuel écart n'est donc que cosmétique, pas fonctionnel.
            // domaine de la plateforme déduit de l'adresse du back-office (admin.qomerxia.com → qomerxia.com)
            platformDomain: window.location.hostname.split('.').length > 2 ? window.location.hostname.split('.').slice(1).join('.') : 'qomerxia.com',
        }
    },
    computed: {
        ...mapGetters('landlord', ['countries', 'sectors', 'societies', 'currentSociety']),
    },
    watch: {
        'form.name'(value) {
            if (this.domainTouched) return
            const slug = value
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // accents
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
            this.form.domain = slug ? `${slug}.${this.platformDomain}` : ''
        },
        societyMode(value) {
            if (value === 'new' && !this.lockedToSociety) {
                this.clearSociety()
            }
        },
    },
    mounted() {
        this.fetchCountries()
        this.fetchSectors()
        this.$nextTick(() => {
            if (typeof KTImageInput !== 'undefined') KTImageInput.init()
        })

        // Arrivée depuis "Ajouter une boutique" sur la fiche société : la
        // société est déjà connue, pas besoin de la rechercher.
        const societyId = this.$route.query.society_id
        if (societyId) {
            this.societyMode = 'existing'
            this.lockedToSociety = true
            this.form.society_id = societyId
            this.fetchSociety(societyId).then(() => {
                this.selectedSociety = this.currentSociety
            })
        }
    },
    methods: {
        ...mapActions('landlord', ['createShop', 'fetchCountries', 'fetchSectors', 'fetchSocieties', 'fetchSociety']),

        onSocietySearch() {
            clearTimeout(this.societySearchTimeout)
            this.societySearchTimeout = setTimeout(() => {
                if (!this.societySearch) {
                    this.societyResults = []
                    return
                }
                this.fetchSocieties({ search: this.societySearch, per_page: 10 }).then(() => {
                    this.societyResults = this.societies
                })
            }, 400)
        },

        selectSociety(society) {
            this.selectedSociety = society
            this.form.society_id = society.id
            this.societyResults = []
            this.societySearch = ''
        },

        clearSociety() {
            this.selectedSociety = null
            this.form.society_id = ''
        },

        onFileChange(event, field) {
            const file = event.target.files[0] || null
            this.files[field] = file
            if (field === 'logo') {
                this.logoPreviewing = file ? URL.createObjectURL(file) : null
            } else if (field === 'favicon') {
                this.faviconPreviewing = file ? URL.createObjectURL(file) : null
            }
        },

        removeImageFile(field) {
            this.files[field] = null
            if (field === 'logo') this.logoPreviewing = null
            else if (field === 'favicon') this.faviconPreviewing = null
        },

        onSubmit() {
            if (this.societyMode === 'existing' && !this.form.society_id) {
                window.toastr['error'](i18n.global.t('landlord.societies.please_select'))
                return
            }

            this.submitting = true
            this.errors = {}

            const formData = new FormData()

            Object.keys(this.form).forEach(key => {
                if (this.form[key] !== null && this.form[key] !== '') {
                    formData.append(key, this.form[key])
                }
            })

            Object.keys(this.phoneCodes).forEach(key => {
                if (this.phoneCodes[key] !== null && this.phoneCodes[key] !== '') {
                    formData.append(key, this.phoneCodes[key])
                }
            })

            Object.keys(this.files).forEach(key => {
                if (this.files[key]) {
                    formData.append(key, this.files[key])
                }
            })

            this.createShop(formData).then(() => {
                window.toastr['success'](i18n.global.t('landlord.shops.created'))
                if (this.societyMode === 'existing' && this.form.society_id) {
                    this.$router.push(`/landlord/societies/${this.form.society_id}`)
                } else {
                    this.$router.push('/landlord/shops')
                }
            }).catch((err) => {
                if (err.response && err.response.status === 422) {
                    this.errors = err.response.data.errors || {}
                }
            }).finally(() => {
                this.submitting = false
            })
        },
    },
}
</script>
